import React from 'react';
import { inject, observer } from 'mobx-react';
import type { AppStore } from '@stores/app/store';
import type { SiteAction } from '@stores/site/action';
import type { UserAction } from '@stores/user/action';
import type { PhotographyAction } from '@stores/photography/action';
import { BasePage } from '@components/Base/BasePage';
import styles from './index.module.scss';
import type { BasePageProps, BasePageState } from '../../../../types/base';
import { withRouter } from 'react-router-dom';
import { RouteComponentProps } from 'react-router-dom';
import { BASE_IMG_URL, livePhotoImage, photographyImage, photographyVideoImage } from '@images/imageUrls';
import PhotographyMediaIcon from '@components/photographyMediaIcon';
import PhotographyVideoPlayer from '@components/photographyVideoPlayer';
import PhotographyDetailPager, { PhotographyVideoWarmup, PhotographyWorkListBackdrop } from '@components/photographyDetailPager';
import { isVideoWork, getPhotographyVideoUrl, getVideoAiText, canDeletePhotographyWork } from '@utils/photographyVideo';
import Toast from '@components/Toast';
import WaterfallFlow from '@components/waterfallFlow';
import PhotographyList from '@components/photographyList';
import { px2rem } from '@utils/px2rem';
import TabNav from '@components/tabNav';
import parse from 'html-react-parser';
import CommonPopupNew from "@components/commonPopupNew";
import classNames from 'classnames';
import SharePosterPopup from './components/SharePosterPopup';
import Avatar from '@components/avatar';
import { Image } from 'antd';
import { display } from 'html2canvas/dist/types/css/property-descriptors/display';
import updateImageListSrc from '@utils/update-image-list-src';

const LIVE_ICON_URL = livePhotoImage.liveIcon;
const SOUND_CLOSE_ICON_URL = livePhotoImage.volumeClose;
const SOUND_OPEN_ICON_URL = livePhotoImage.volume;
type DetailListSource = 'works' | 'myWorks' | 'myVotes';

interface RouteParams {
    code: string;
}
interface PhotographyH5PageProps extends BasePageProps, RouteComponentProps<RouteParams> {
    app?: AppStore;
    site?: SiteAction;
    user?: UserAction;
    photography?: PhotographyAction;
}
interface PhotographyH5PageState extends BasePageState {
    downloadAppStatus: boolean; // 是否显示下载入口，用来记录下载入口的初始化状态，返回到其他页面后恢复下载入口的显示状态
    headerShowStatus: boolean;  // 是否显示header，用来记录header的初始化状态，返回到其他页面后恢复header的显示状态
    photographyDetail: any
    rulePopupStatus: boolean
    navList: any
    navCurrentIndex: any
    navCurrentType: any
    photoList: any[];// 照片列表
    currentPage: any
    hasMore: boolean; // 是否还有更多数据
    scrollY: any
    scrollDirection: any
    tipPopupStatus: boolean
    currentVoteItem: any
    currentOpenAppType: any
    isFoldWorkList: boolean
    sharePosterVisible: boolean
    shareWorkItem: any
    myWorksList: any
    myWorkPage: any
    myWorkHasMore: boolean
    myWorkLoading: boolean
    myWorkError: string
    myVotesList: any
    myVotePage: any  // 我的投票列表分页
    myVoteHasMore: boolean
    myVoteLoading: boolean
    myVoteError: string
    manageWorkPopupStatus: boolean
    manageCurrentWorkData: any
    deleteWorkPopupStatus: boolean
    deletingWork: boolean
    closeDetailTrigger: number  // 触发子组件关闭详情的标记
    userLatestWork: any
    currentImgDetail: any;  // 当前点击的图片数据
    currentImgDetailShowStatus: boolean // 当前点击的图片信息弹框是否显示
    currentImgDetailLoading: boolean
    weiXinTipMask: boolean  // 微信内弹框提示浏览器打开
    availableVotes: any
    floatingLikes: Array<{ id: number, left: number, xOffset: number, delay: number }> // 飘动的爱心图标列表
    likeAnimationVisible: boolean
    shareLinkType: string  // 分享链接类型，有值时显示"我要参赛"，无值时显示"为作者拉票"
    detailLivePlaying: boolean
    detailLiveMuted: boolean
    isVivoBrowserStatus: boolean
}

@inject('app', 'site', 'user', 'photography')
@observer
class PhotographyH5Page extends BasePage<PhotographyH5PageProps, PhotographyH5PageState> {
    photographyCode:any ="";  // 大赛Id
    private navBoxRef: HTMLDivElement | null = null;
    private detailLiveVideoRef: HTMLVideoElement | null = null;
    private detailRequestId = 0;
    private worksRequestId = 0;
    private isLoadingWorks = false;
    private myWorksRequestId = 0;
    private myVotesRequestId = 0;
    private isLoadingMyWorks = false;
    private videoProgressRef = React.createRef<HTMLDivElement>();
    private votingWorkId: any = null;
    private deletingWorkId: any = null;
    private detailListSource: DetailListSource = 'works';
    private detailEntryWork: any = null;
    private detailNavigationVersion = 0;
    private detailSwitching = false;
    private detailCache = new Map<string, { data: any; time: number }>();
    private detailPrefetches = new Map<string, Promise<any>>();
    private detailCacheVersion = 0;
    private detailPageRequests: Partial<Record<DetailListSource, Promise<void>>> = {};
    goSearchPage = () => {
        if (!this.photographyCode) return;
        this.props.history.push(`/photographySearch/${this.photographyCode}`);
    }

    isLoadingMyVotes: boolean = false;  // 加载投票列表的锁
    likeAnimationHideTimer: ReturnType<typeof setTimeout> | null = null;
    state: PhotographyH5PageState = {
        loading: false,
        error: null,
        pageLoaded: false,
        downloadAppStatus: false, // 是否显示下载入口
        headerShowStatus: false,  // 是否显示header
        hasMore: true, // 是否还有更多数据
        photographyDetail: {},  // 大赛详情
        rulePopupStatus: false, // 大赛规则弹框是否显示
        navList: [
            {name: '最新作品', id: 0, type: 'id'},
            {name: '人气排行', id: 1, type: 'ranking'},
            {name: '我的', id: 2, type: 'my'},
        ],
        navCurrentIndex: 0, // 导航当前选中索引
        navCurrentType: 'id', // 导航当前选中类型
        photoList: [],// 投稿作品列表
        currentPage: 1, // 投稿列表分页
        tipPopupStatus: false,  // 提示弹框
        currentVoteItem: {},    // 点击投票时，当前选中的投票数据或者路由携带分享的workId时，打开的作品详情数据
        scrollY: 0, // 滚动高度
        scrollDirection: null,    // 滚动方向
        currentOpenAppType: 1,  // 打开app的类型，1是去投稿（打开app大赛详情），2是去投票（打开某个作品详情）
        isFoldWorkList: true,  // 默认折叠，最多展示两件作品
        sharePosterVisible: false, // 是否显示拉票海报
        shareWorkItem: {}, // 当前拉票作品
        myWorksList: [],    // 我的作品列表
        myWorkPage: 1,  // 我的作品列表分页
        myWorkHasMore: false,   // 第一页成功后再允许加载更多
        myWorkLoading: false,
        myWorkError: '',
        myVotesList: [],    // 我的投票列表
        myVotePage: 1,  // 我的投票列表分页
        myVoteHasMore: false,
        myVoteLoading: false,
        myVoteError: '',
        manageWorkPopupStatus: false,   // 作品管理弹框
        manageCurrentWorkData: {},  // 点击管理时，当前选中的作品数据
        deleteWorkPopupStatus: false,   // 删除作品弹框是否显示
        deletingWork: false,
        closeDetailTrigger: 0,  // 触发子组件关闭详情的标记
        userLatestWork: {}, // 当前用户发布的最后的作品
        currentImgDetail: {},   // 当前查看的图片详情数据
        currentImgDetailShowStatus: false,  // 当前查看的图片信息弹框是否显示
        currentImgDetailLoading: false,
        weiXinTipMask: false,   // 微信内弹框提示浏览器打开
        availableVotes: 0,  // 余票
        floatingLikes: [],  // 飘动的点赞图标
        likeAnimationVisible: false,
        shareLinkType: '',  // 分享链接类型
        detailLivePlaying: false,
        detailLiveMuted: true,
        isVivoBrowserStatus: false, // 是否是vivo浏览器
        
    };
    componentDidMount() {
        const { app, user, site, match, photography } = this.props;
        const routerParams = new URLSearchParams(this.props.location.search);
        const workId: any = routerParams?.get('workId') || '';
        const actionType: any = routerParams?.get('actionType') || ''; // 获取分享链接参数
        // console.log(workId, 'workId----');
        if(photography && workId) {
            this.getPhotographyWorksDetailData(workId);
        }
        
        // 设置分享链接类型
        if(actionType) {
            this.setState({
                shareLinkType: actionType
            });
        }
        this.photographyCode = match.params.code;
        this.getPhotoDetailData();
        
        if (site) {
            this.setState({
                downloadAppStatus: site.getAppDownloadShowStatus(),
                headerShowStatus: site.getShowHeader(),

            }, () => {
                site.setShowHeader(false);
            })
        }
        // 添加滚动监听
        window.addEventListener('scroll', this.handleScroll);
        // 监听页面从其他页面返回（如个人中心）
        window.addEventListener('pageshow', this.handlePageShow);
        // 监听页面可见性变化（从其他页面返回时触发）
        document.addEventListener('visibilitychange', this.handleVisibilityChange);
        // 判断是不是vivo浏览器
        this.isVivoBrowser();
        super.componentDidMount();
    }

    componentDidUpdate(prevProps: PhotographyH5PageProps, prevState: any) {
        // 同步 site.appDownloadShowStatus 到本地 state
        const { site } = this.props;
        if (site && site.appDownloadShowStatus !== prevProps.site?.appDownloadShowStatus) {
            this.setState({ downloadAppStatus: site.appDownloadShowStatus });
        }
        // 当从其他页面返回时，刷新当前列表并重置到初始状态
        const { location } = this.props;
        const { location: prevLocation } = prevProps;
        const { currentImgDetailShowStatus } = this.state;
        const { currentImgDetailShowStatus: prevShowStatus } = prevState;
        
        // 判断是否需要重置列表：
        // 仅从其他页面返回时重置（location.key 变化），不包括图片详情弹框关闭
        const shouldResetList = location.key !== prevLocation.key;
        // console.log(shouldResetList, '是否需要刷新');
        if (shouldResetList) {
            this.resetListToFirstPage();
        }

        // 当详情弹窗显示状态变化时，更新浏览器标题
        if (currentImgDetailShowStatus !== prevShowStatus || this.state.currentImgDetail?.name !== prevState.currentImgDetail?.name) {
            if (currentImgDetailShowStatus) {
                document.title = this.state.currentImgDetail?.name;
            } else {
                // 恢复原来的标题（使用大赛名称或默认标题）
                document.title = this.state.photographyDetail?.name || '摄影大赛';
            }
        }
        if (currentImgDetailShowStatus && (this.state.currentImgDetail !== prevState.currentImgDetail || this.state.photoList !== prevState.photoList || this.state.myWorksList !== prevState.myWorksList || this.state.myVotesList !== prevState.myVotesList || this.state.loading !== prevState.loading || this.state.myWorkLoading !== prevState.myWorkLoading || this.state.myVoteLoading !== prevState.myVoteLoading)) this.warmDetailNeighbors();
    }
    componentWillUnmount() {
        this.detailRequestId++;
        this.detailNavigationVersion++;
        this.clearDetailCache();
        this.invalidateWorksListRequests();
        this.invalidateMyListRequests();
        // 移除滚动监听
        window.removeEventListener('scroll', this.handleScroll);
        // 移除页面显示监听
        window.removeEventListener('pageshow', this.handlePageShow);
        // 移除页面可见性监听
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        this.pauseDetailLive(false);
        if (this.likeAnimationHideTimer) {
            clearTimeout(this.likeAnimationHideTimer);
            this.likeAnimationHideTimer = null;
        }
    }

    /**
     * 判断当前浏览器是否为 vivo 浏览器
     * @returns {boolean} true: 是 vivo 浏览器, false: 不是
     */
    isVivoBrowser() {
        if (typeof navigator === 'undefined') {
            return false;
        }
        
        const ua = navigator.userAgent.toLowerCase();
        // 优先判断 VivoBrowser 关键词，其次判断 vivo 关键词作为兼容
        let isVivoBrowser = /vivobrowser/i.test(ua) || 
            ( /vivo/i.test(ua) && !/micromessenger/i.test(ua) && !/qq/i.test(ua) );
            this.setState({
                isVivoBrowserStatus: isVivoBrowser
            })
        return /vivobrowser/i.test(ua) || 
            ( /vivo/i.test(ua) && !/micromessenger/i.test(ua) && !/qq/i.test(ua) );
            
    }

    // 页面从其他页面返回时触发（如从个人中心返回）
    handlePageShow = (event: PageTransitionEvent) => {
        // persisted 为 true 表示是从缓存中恢复（如返回按钮）
        if (event.persisted && !this.state.currentImgDetailShowStatus) {
            this.resetListToFirstPage();
        }
    };

    // 页面可见性变化时触发（从其他页面返回）
    handleVisibilityChange = () => {
        // 页面变为可见时，重置列表
        if (document.visibilityState === 'visible' && !this.state.currentImgDetailShowStatus) {
            this.resetListToFirstPage();
        }
    };

    // 重置列表到第一页
    resetListToFirstPage = () => {
        this.invalidateWorksListRequests();
        this.invalidateMyListRequests();
        this.setState({
            navCurrentIndex: 0,
            navCurrentType: 'id',
            currentPage: 1,
            hasMore: true,
            photoList: [],
            isFoldWorkList: true,
            myWorksList: [],
            myWorkPage: 1,
            myWorkHasMore: false,
            myWorkLoading: false,
            myWorkError: '',
            myVotesList: [],
            myVotePage: 1,
            myVoteHasMore: false,
            myVoteLoading: false,
            myVoteError: '',
            userLatestWork: {},
        }, () => {
            this.getPhotographyContestsWorksList(false);
        });
    };

    // 获取大赛详情
    getPhotoDetailData = () => {  
        const { photography } = this.props;
        if(photography) {
            photography.getPhotographyContestsDetail(this.photographyCode).then(res => {
                if(res.code == 0) {
                    // if(res.data.status == 2) {  // 如果大赛已结束，跳转到大赛摄影展
                    //     // this.props.history.push(`/photoExhibition`)
                    // } else 
                    if (!this.state.currentImgDetailShowStatus) document.title = res.data?.name;
                    if (res.data.status == 0) {  // 未开始
                        window.ensureBack(() => {});
                    } else {
                        this.setState({
                            photographyDetail: res.data,
                            availableVotes: res.data?.remainingVotes
                        }, () => {
                            if (this.state.navCurrentIndex === 2) {
                                this.getPhotographyContestsMyWorks();
                                this.getPhotographyContestsMyVotes();
                            } else {
                                this.getPhotographyContestsWorksList();
                            }
                        })
                    }
                    
                } else {
                    // Toast.info('服务器错误，请稍后重试')
                }
                
            });
        }
    }

    // 获取某个详情
    getPhotographyWorksDetailData = (workId) => {  
        const requestId = ++this.detailRequestId;
        const { photography } = this.props;
        console.log('获取作品详情111', photography);
        if(photography) {
            this.setState({ currentImgDetailLoading: true });
            this.requestDetailWork(workId).then(res => {
                if (requestId !== this.detailRequestId) return;
                if(res.code == 0) {
                    this.pauseDetailLive();
                    if (!this.detailEntryWork || String(this.detailEntryWork.id) === String(workId)) this.detailEntryWork = res.data;
                    this.setState({
                        currentImgDetail: {
                            ...this.state.currentImgDetail,
                            ...res.data,
                        },
                        currentImgDetailShowStatus: true,
                        currentImgDetailLoading: false,
                        detailLivePlaying: false,
                        detailLiveMuted: true,
                    }, () => {
                        this.maybeAutoPlayDetailLive(res.data);
                    })
                } else {
                    this.setState({ currentImgDetailLoading: false });
                    Toast.info(`${res.msg}`)
                } 
            }).catch(() => {
                if (requestId !== this.detailRequestId) return;
                this.setState({ currentImgDetailLoading: false });
                Toast.info('作品加载失败，请稍后重试');
            });
        }
    }

    // 获取投稿列表
    // isLoadMore: true 表示加载更多，false 表示首次加载/刷新
    getPhotographyContestsWorksList = async (isLoadMore = false) => {
        const { currentPage, navCurrentType, photographyDetail, hasMore } = this.state;
        const { photography } = this.props;
        if (!photography || !photographyDetail.id || this.isLoadingWorks || (isLoadMore && !hasMore)) return;
        const requestId = ++this.worksRequestId;
        const page = isLoadMore ? currentPage + 1 : 1;
        this.isLoadingWorks = true;
        this.setState({ loading: true });
        try {
            const res = await photography.getPhotographyContestsWorks(photographyDetail.id, {
                sort: navCurrentType,
                'page[number]': page,
                'page[size]': 20,
            });
            if (requestId !== this.worksRequestId) return;
            if (res.code !== 0) throw new Error('作品加载失败');
            const newData = res.data?.data || [];
            await new Promise<void>(resolve => this.setState(state => ({
                photoList: isLoadMore ? [...state.photoList, ...newData] : newData,
                currentPage: page,
                hasMore: newData.length > 0 && Boolean(res.data?.page?.hasMore),
                userLatestWork: navCurrentType === 'ranking' && page === 1 ? res.data?.latestWork || {} : state.userLatestWork,
            }), resolve));
        } catch {
            if (requestId === this.worksRequestId) Toast.info('作品加载失败，请稍后重试');
        } finally {
            if (requestId === this.worksRequestId) {
                this.isLoadingWorks = false;
                this.setState({ loading: false });
            }
        }
    }

    invalidateWorksListRequests = () => {
        this.worksRequestId++;
        this.isLoadingWorks = false;
        delete this.detailPageRequests.works;
    };

    // 切换标签或离开页面后，不再接收旧的个人列表请求。
    invalidateMyListRequests = () => {
        this.myWorksRequestId++;
        this.myVotesRequestId++;
        this.isLoadingMyWorks = false;
        this.isLoadingMyVotes = false;
        delete this.detailPageRequests.myWorks;
        delete this.detailPageRequests.myVotes;
    };

    // 个人列表接口使用大赛数字 ID，分享码只用于查询大赛详情。
    getPhotographyContestsMyWorks = async (isLoadMore = false) => {
        const { photography } = this.props;
        const { photographyDetail, myWorkPage, myWorkHasMore } = this.state;
        if (!photography || !photographyDetail.id || this.isLoadingMyWorks) return;
        if (isLoadMore && !myWorkHasMore) return;

        const requestId = ++this.myWorksRequestId;
        const page = isLoadMore ? myWorkPage + 1 : 1;
        this.isLoadingMyWorks = true;
        this.setState({ myWorkLoading: true, myWorkError: '' });
        try {
            const res = await photography.getPhotographyContestsMyWorks(photographyDetail.id, {
                'page[number]': page,
                'page[size]': 20,
                sort: 'ranking',
            });
            if (requestId !== this.myWorksRequestId) return;
            if (res.code !== 0) throw new Error('作品加载失败，请重试');
            const newData = res.data?.data || [];
            await new Promise<void>(resolve => this.setState(state => ({
                myWorksList: isLoadMore ? [...state.myWorksList, ...newData] : newData,
                myWorkPage: page,
                myWorkHasMore: newData.length > 0 && Boolean(res.data?.page?.hasMore),
            }), resolve));
        } catch {
            if (requestId === this.myWorksRequestId) {
                this.setState({ myWorkError: '作品加载失败，请重试' });
            }
        } finally {
            if (requestId === this.myWorksRequestId) {
                this.isLoadingMyWorks = false;
                this.setState({ myWorkLoading: false });
            }
        }
    };

    loadMoreMyWorksList = () => {
        if (!this.state.myWorkError) this.getPhotographyContestsMyWorks(true);
    };

    getPhotographyContestsMyVotes = async (isLoadMore = false) => {
        const { photography } = this.props;
        const { photographyDetail, myVotePage, myVoteHasMore } = this.state;
        if (!photography || !photographyDetail.id || this.isLoadingMyVotes) return;
        if (isLoadMore && !myVoteHasMore) return;

        const requestId = ++this.myVotesRequestId;
        const page = isLoadMore ? myVotePage + 1 : 1;
        this.isLoadingMyVotes = true;
        this.setState({ myVoteLoading: true, myVoteError: '' });
        try {
            const res = await photography.getPhotographyContestsMyVotes(photographyDetail.id, {
                'page[number]': page,
                'page[size]': 20,
            });
            if (requestId !== this.myVotesRequestId) return;
            if (res.code !== 0) throw new Error('投票记录加载失败，请重试');
            const newData = res.data?.data || [];
            await new Promise<void>(resolve => this.setState(state => ({
                myVotesList: isLoadMore ? [...state.myVotesList, ...newData] : newData,
                myVotePage: page,
                myVoteHasMore: newData.length > 0 && Boolean(res.data?.page?.hasMore),
            }), resolve));
        } catch {
            if (requestId === this.myVotesRequestId) {
                this.setState({ myVoteError: '投票记录加载失败，请重试' });
            }
        } finally {
            if (requestId === this.myVotesRequestId) {
                this.isLoadingMyVotes = false;
                this.setState({ myVoteLoading: false });
            }
        }
    };

    loadMoreMyVotesList = () => {
        if (!this.state.myVoteError) this.getPhotographyContestsMyVotes(true);
    };

    renderMyListFeedback = (type: 'works' | 'votes') => {
        const isWorks = type === 'works';
        const loading = isWorks ? this.state.myWorkLoading : this.state.myVoteLoading;
        const error = isWorks ? this.state.myWorkError : this.state.myVoteError;
        if (loading) return <div className={styles.myListFeedback} role="status">加载中...</div>;
        if (!error) return null;
        return <div className={styles.myListFeedback} role="alert">
            <span>{error}</span>
            <button type="button" aria-label={isWorks ? '重试我的作品' : '重试我的投票'} onClick={() => {
                if (isWorks) this.getPhotographyContestsMyWorks(this.state.myWorksList.length > 0);
                else this.getPhotographyContestsMyVotes(this.state.myVotesList.length > 0);
            }}>重试</button>
        </div>;
    };

    // 类型切换
    navClick = (item:any, index:any) => {
        let { photographyDetail } = this.state;
        if(index == 2) {
            if (!this.props.user?.userInfo?.code) {
                const { user } = this.props as any;
                if (user && typeof user.checkLogin === 'function') {
                    const isLogin = user.checkLogin();
                    if (!isLogin) {
                        return;
                    }
                }
                Toast.info({ content: '请先登录!' });
                return;
            }
        }
        this.invalidateMyListRequests();
        this.invalidateWorksListRequests();
        this.detailNavigationVersion++;
        this.setState({
            navCurrentIndex: index,
            navCurrentType: item.type,
            photoList: [],
            currentPage: 1,
            loading: false,
            hasMore: true,
            isFoldWorkList: true,
            myWorksList: [],
            myWorkPage: 1,
            myWorkHasMore: false,
            myWorkLoading: index === 2,
            myWorkError: '',
            myVotesList: [],
            myVotePage: 1,
            myVoteHasMore: false,
            myVoteLoading: index === 2,
            myVoteError: '',
            userLatestWork: {},
        }, () => {
            if(index == 2) {    // 我的
                // if (!this.props.user?.userInfo?.code) {
                //     const { user } = this.props as any;
                //     if (user && typeof user.checkLogin === 'function') {
                //         const isLogin = user.checkLogin();
                //         if (!isLogin) {
                //             console.log('[SetPost] User not logged in, aborting init (checkLogin handled redirect)');
                //             return;
                //         }
                //     }
                //     Toast.info({ content: '请先登录!' });
                //     return;
                // }
                // 请求我的作品和我的投票列表
                this.getPhotographyContestsMyWorks();
                this.getPhotographyContestsMyVotes();
            } else if(index == 1) {    // 人气排行
                if(photographyDetail.isRankingEnabled) {
                    this.getPhotographyContestsWorksList();
                }  
            } else if(index  == 0) {    // 最新作品
                this.getPhotographyContestsWorksList();
            }
            
        })
    }

    // 处理滚动事件，检测是否触底
    handleScroll = () => {
        const { loading, hasMore, navCurrentIndex, myVoteHasMore, scrollY } = this.state;

        // 计算当前滚动位置
        const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
        const scrollHeight = document.documentElement.scrollHeight || document.body.scrollHeight;
        const clientHeight = document.documentElement.clientHeight || document.body.clientHeight;

        if (scrollTop !== scrollY) {
            this.setState({
                scrollY: scrollTop
            });
        }

        // 如果正在加载，则不处理
        if (loading) return;

        // 距离底部100px时触发加载
        if (scrollTop + clientHeight >= scrollHeight - 100) {
            // 在"我的"标签页时，只触底加载投票列表（作品列表通过点击按钮加载）
            if (navCurrentIndex === 2) {
                if (myVoteHasMore) {
                    this.loadMoreMyVotesList();
                }
                return;
            }

            // 其他标签页，使用默认的加载逻辑
            if (!hasMore) return;
            this.handleLoadMore();
        }
    }

    // 加载更多
    handleLoadMore = async () => {
        const { loading, hasMore } = this.state;
        // 如果正在加载或没有更多数据，则直接返回
        if (loading || !hasMore) return;

        console.log('加载更多数据');

        // 调用加载更多（getPhotographyContestsWorksList 内部会管理 loading 状态）
        await this.getPhotographyContestsWorksList(true);
    }
    

    // 在组件中计算动态top值
    // getDynamicTop = () => {
    //     const { scrollDirection } = this.state;
    //     // 下载入口隐藏时：下滑0px，上滑88px
    //     return scrollDirection === 'down' ? px2rem(88) : px2rem(176);
        
    // }

    // 关闭弹框
    onHidePopup = () => {
        console.log('关闭');
        this.setState({
            rulePopupStatus: false,
            tipPopupStatus: false,
            manageWorkPopupStatus: false,
            deleteWorkPopupStatus: false,
        })
    }

    // 打开app去投稿
    handleOpenAppConfirm = () => {
        let { currentOpenAppType, currentVoteItem } = this.state;
        this.setState({
            tipPopupStatus: false,
        })
        if(this.isWeixin()) {
            this.setState({
                weiXinTipMask: true
            });
            return false;
        }
        
        console.log(currentOpenAppType, '为1是大赛详情，为2是作品详情');
        if(currentOpenAppType == 1) {   // 打开大赛详情
            window.location.href = `vivoclub://com.vivo.club/deep?page=photography_detail_page&code=${this.photographyCode}`;
            // 3秒后如果应用没有打开，跳转到下载页
            setTimeout(() => {
                // this.props.history.push(`/downloadAppEnter`);
                window.open(`https://m.malink.cn/s/ABbE73`)
            }, 3000);
        } else {    // 打开某个作品详情
            const deepLink = `vivoclub://com.vivo.club/deep?page=photography_compositon_detail&id=${currentVoteItem.id}`;
            window.open(deepLink);
            // 3秒后如果应用没有打开，跳转到下载页
            setTimeout(() => {
                this.props.history.push(`/downloadAppEnter?deepLink=${encodeURIComponent(deepLink)}`);
                // window.open(`https://m.malink.cn/s/ABbE73`)
            }, 3000);
        }
    }

    // 微信覆盖提示
    isWeixinXml() {
        return (
            <div className={styles.opAlertTable} onClick={() => {
                this.setState({ weiXinTipMask: false } as any);
            }}>
                <div className={styles.alertWhite}>
                    <div className={styles.inter}></div>
                    点击右上角选择在默认浏览器中打开
                </div>
            </div>
        );
    }
    
    isWeixin() {
        var ua = navigator.userAgent.toLowerCase();
        return ua.indexOf('micromessenger') !== -1;
    }

    // 投票
    submitPhotographyWorksVoteClick = (workId) => {
        if (this.votingWorkId != null) return;
        let { availableVotes } = this.state;
        let { photography } = this.props;
        if(photography) {
            this.votingWorkId = workId;
            photography.submitPhotographyWorksVoteData(workId).then(res => {
                console.log(res, '投票结果');
                if(res.code == 0) {
                    Toast.info(`感谢你的宝贵一票，今日剩余${Math.max(0, availableVotes - 1)}票`);

                    if (this.likeAnimationHideTimer) {
                        clearTimeout(this.likeAnimationHideTimer);
                        this.likeAnimationHideTimer = null;
                    }
                    this.setState({
                        floatingLikes: [],
                        likeAnimationVisible: true
                    }, () => {
                        // 生成7-8个飘动的点赞图标，逐个延迟添加到 state
                        const count = 7 + Math.floor(Math.random() * 2); // 7或8个
                        for (let i = 0; i < count; i++) {
                            setTimeout(() => {
                                const newLike = {
                                    id: Date.now() + Math.random(),
                                    left: -60 + Math.random() * 120,
                                    xOffset: -50 + Math.random() * 100,
                                    delay: 0
                                };
                                this.setState((prevState) => ({
                                    floatingLikes: [...(prevState?.floatingLikes || []), newLike]
                                }));
                            }, i * 150); // 每个图标间隔150ms添加
                        }

                        this.likeAnimationHideTimer = setTimeout(() => {
                            this.setState({
                                floatingLikes: [],
                                likeAnimationVisible: false
                            });
                            this.likeAnimationHideTimer = null;
                        }, 3200);
                    });

                    // 更新 photoList 中对应作品的 isVoted 状态和 voteCount
                    const updatedPhotoList = this.state.photoList.map(item => {
                        if (String(item.id) === String(workId)) {
                            return {
                                ...item,
                                isVoted: true,
                                voteCount: Number(item.voteCount || 0) + 1
                            };
                        }
                        return item;
                    });
                    
                    // 更新 currentImgDetail 中的 isVoted 状态和 voteCount
                    const latestDetail = this.state.currentImgDetail;
                    let updatedCurrentImgDetail = latestDetail;
                    if(latestDetail && String(latestDetail.id) === String(workId)) {
                        updatedCurrentImgDetail = { 
                            ...latestDetail,
                            isVoted: true,
                            voteCount: Number(latestDetail.voteCount || 0) + 1
                        };
                    }
                    const cached = this.getCachedDetail(workId);
                    if (cached) this.detailCache.set(String(workId), { data: { ...cached, isVoted: true, voteCount: Number(cached.voteCount || 0) + 1 }, time: Date.now() });
                    this.setState({
                        availableVotes: Math.max(0, availableVotes - 1),
                        photoList: updatedPhotoList,
                        currentImgDetail: updatedCurrentImgDetail
                    })
                } else {
                    Toast.info(res.msg || '投票未成功，请稍后重试');
                }
            }).catch(() => Toast.info('投票未成功，请稍后重试')).finally(() => {
                this.votingWorkId = null;
            });
        } else {
            // 请求失败
            
        }
    }   
    // 点击投票提示
    voteClick = (item) => {
        if (Number(this.state.photographyDetail.status) === 2) {
            Toast.info('活动已结束');
            return;
        }
        if (!this.props.user?.userInfo?.code) {
            const { user } = this.props as any;
            if (user && typeof user.checkLogin === 'function') {
                const isLogin = user.checkLogin();
                if (!isLogin) {
                    console.log('[SetPost] User not logged in, aborting init (checkLogin handled redirect)');
                    return;
                }
            }
            Toast.info({ content: '请先登录!' });
            return;
        }
        if(item.isVoted) {
            Toast.info('每天只能为同一个作品投一次');
        } else {
            this.submitPhotographyWorksVoteClick(item.id);
            // this.setState({
            //     tipPopupStatus: true,
            //     currentVoteItem: item,
            //     currentOpenAppType: 2
            // })
        }
    }
    // 点击详情右上角管理按钮，显示管理弹框
    manageCurrentWork = (data) => {
        if (!canDeletePhotographyWork(data, this.props.user?.userInfo, this.state.photographyDetail.status)) return;
        this.setState({
            manageCurrentWorkData: data,
            manageWorkPopupStatus: true,
        }) 
    }

    // 删除作品二次确认弹框显示
    deleteWorkOpenPopup = () => {
        this.setState({
            deleteWorkPopupStatus: true,
            manageWorkPopupStatus: false,
        }) 
    }

    // 删除当前选择的作品
    deleteWorkConfirm = async () => {
        const { manageCurrentWorkData } = this.state;
        const { photography } = this.props;
        if (!photography || this.deletingWorkId != null || !manageCurrentWorkData?.id
            || !canDeletePhotographyWork(manageCurrentWorkData, this.props.user?.userInfo, this.state.photographyDetail.status)) return;
        const id = manageCurrentWorkData.id;
        this.deletingWorkId = id;
        this.setState({ deletingWork: true });
        try {
            const result = await photography.deletePhotographyWorkData(id);
            if (result.code !== 0) {
                Toast.info(result.msg || '删除失败，请稍后重试');
                return;
            }
            const keepWork = (work: any) => String(work.id) !== String(id);
            this.setState(state => ({
                manageWorkPopupStatus: false,
                deleteWorkPopupStatus: false,
                myWorksList: state.myWorksList.filter(keepWork),
                photoList: state.photoList.filter(keepWork),
                myVotesList: state.myVotesList.filter(keepWork),
                userLatestWork: String(state.userLatestWork?.id) === String(id) ? {} : state.userLatestWork,
            }));
            this.closeCurrentImgDetail();
            Toast.info('作品删除成功');
        } catch {
            Toast.info('删除失败，请稍后重试');
        } finally {
            this.deletingWorkId = null;
            this.setState({ deletingWork: false });
        }
    }

    getShareUrl = (item) => {
        const workId = item?.id ? `?workId=${item.id}&actionType=join` : '?actionType=join';
        return `${window.location.origin}/photography/${this.photographyCode}${workId}`;
    }

    getTopVoteCount = () => {
        const { photoList } = this.state;
        if (!Array.isArray(photoList) || photoList.length === 0) {
            return 0;
        }
        return photoList.reduce((max, current) => {
            return Math.max(max, Number(current?.voteCount || 0));
        }, 0);
    }

    closeSharePoster = () => {
        this.setState({
            sharePosterVisible: false,
            shareWorkItem: {}
        })
    }

    // 点击为TA拉票
    canvassForVotesClick = (item) => {
       console.log(item, '调起分享');
       let { photographyDetail } = this.state;
       if(photographyDetail.status == 2) {  // 活动已结束
            Toast.info('活动已结束');
            return false;
       }
       if (isVideoWork(item) && Number(item?.status) !== 1) return;
       this.setState({
            sharePosterVisible: true,
            shareWorkItem: item || {}
       })
    }

    // 点击作品打开详情
    currentItemClick = (item, source: DetailListSource = this.state.navCurrentIndex === 2 ? 'myWorks' : 'works', keepNavigation = false) => {
       this.detailRequestId++;
       this.detailNavigationVersion++;
       this.detailSwitching = false;
       if (!keepNavigation) {
           this.clearDetailCache();
           this.detailListSource = source;
           this.detailEntryWork = item;
       }
       this.pauseDetailLive();
       if (item?.id) {
           (this.props as any).site?.setCurrentPhotographyWorkId(item.id);
       }
       const cached = this.getCachedDetail(item?.id);
       if (!cached && item?.id && this.props.photography) void this.requestDetailWork(item.id).catch(() => {});
       this.setState({
        currentImgDetail: cached ? { ...item, ...cached } : item,
        currentImgDetailShowStatus: true,
        currentImgDetailLoading: !cached && !!item?.id && !!this.props.photography,
        detailLivePlaying: false,
        detailLiveMuted: true,
        likeAnimationVisible: false,
        floatingLikes: [],
       }, () => {
        if (item?.id) {
            this.getPhotographyWorksDetailData(item.id);
            return;
        }
        this.maybeAutoPlayDetailLive(item);
       })
       
    }

    clearDetailCache = () => {
        this.detailCacheVersion++;
        this.detailCache.clear();
        this.detailPrefetches.clear();
    };

    getCachedDetail = (id: any) => {
        const cached = this.detailCache.get(String(id));
        return cached && Date.now() - cached.time < 30000 ? cached.data : undefined;
    };

    requestDetailWork = (id: any): Promise<any> => {
        const key = String(id);
        const cached = this.getCachedDetail(key);
        if (cached) return Promise.resolve({ code: 0, data: cached });
        const pending = this.detailPrefetches.get(key);
        if (pending) return pending;
        const version = this.detailCacheVersion;
        const promise = this.props.photography!.getPhotographyWorksDetail(id).then(res => {
            if (res.code === 0 && version === this.detailCacheVersion) {
                this.detailCache.delete(key);
                this.detailCache.set(key, { data: res.data, time: Date.now() });
                if (this.detailCache.size > 6) this.detailCache.delete(this.detailCache.keys().next().value);
            }
            return res;
        }).finally(() => {
            if (this.detailPrefetches.get(key) === promise) this.detailPrefetches.delete(key);
        });
        this.detailPrefetches.set(key, promise);
        return promise;
    };

    warmDetailNeighbors = () => {
        if (!this.props.photography || !this.state.currentImgDetailShowStatus) return;
        const list = this.getDetailList();
        const index = list.items.findIndex(item => String(item.id) === String(this.state.currentImgDetail?.id));
        if (index < 0 || list.loadedCount === 0) return;
        const version = this.detailCacheVersion;
        [list.items[index - 1], list.items[index + 1]].forEach(item => {
            if (!item?.id || this.getCachedDetail(item.id)) return;
            this.requestDetailWork(item.id).then(res => {
                if (version === this.detailCacheVersion && this.state.currentImgDetailShowStatus && res.code === 0 && getPhotographyVideoUrl(res.data) !== getPhotographyVideoUrl(item)) this.forceUpdate();
            }).catch(() => {}); // 预取失败由实际打开该作品时重试、提示。
        });
        if (index >= list.items.length - 2 && list.hasMore && !list.loading) void list.loadMore();
    };

    getDetailList = () => {
        const { photoList, hasMore, myWorksList, myWorkHasMore, myVotesList, myVoteHasMore } = this.state;
        const source = this.detailListSource;
        const items = source === 'myWorks' ? myWorksList : source === 'myVotes' ? myVotesList : photoList;
        // 分享直达或排行榜悬浮卡片的作品可能不在当前页，仍保留它作为本次浏览入口。
        const entry = this.detailEntryWork || this.state.currentImgDetail;
        const works = entry?.id && !items.some(item => String(item.id) === String(entry.id)) ? [entry, ...items] : items;
        return {
            items: works,
            loadedCount: items.length,
            hasMore: source === 'myWorks' ? myWorkHasMore : source === 'myVotes' ? myVoteHasMore : hasMore,
            loading: source === 'myWorks' ? this.isLoadingMyWorks : source === 'myVotes' ? this.isLoadingMyVotes : this.isLoadingWorks,
            loadMore: () => {
                const pending = this.detailPageRequests[source];
                if (pending) return pending;
                const append = items.length > 0;
                const request = source === 'myWorks' ? this.getPhotographyContestsMyWorks(append) : source === 'myVotes' ? this.getPhotographyContestsMyVotes(append) : this.getPhotographyContestsWorksList(append);
                this.detailPageRequests[source] = request;
                void request.finally(() => { if (this.detailPageRequests[source] === request) delete this.detailPageRequests[source]; });
                return request;
            },
        };
    };

    prepareDetailWork = async (direction: -1 | 1): Promise<boolean> => {
        const { currentImgDetail, currentImgDetailShowStatus, manageWorkPopupStatus, deleteWorkPopupStatus, sharePosterVisible, tipPopupStatus } = this.state;
        if (!currentImgDetailShowStatus || !currentImgDetail?.id || this.detailSwitching || manageWorkPopupStatus || deleteWorkPopupStatus || sharePosterVisible || tipPopupStatus) return false;
        const version = this.detailNavigationVersion;
        this.detailSwitching = true;
        try {
            let list = this.getDetailList();
            let index = list.items.findIndex(item => String(item.id) === String(currentImgDetail.id));
            if (direction === 1 && index === list.items.length - 1 && list.hasMore) {
                const pending = this.detailPageRequests[this.detailListSource];
                if (list.loading && !pending) return false;
                await (pending || list.loadMore());
                if (version !== this.detailNavigationVersion || !this.state.currentImgDetailShowStatus) return false;
                list = this.getDetailList();
                index = list.items.findIndex(item => String(item.id) === String(currentImgDetail.id));
            }
            const next = list.items[index + direction];
            if (index >= 0 && next) return true;
            else if (direction === -1 || !list.hasMore) Toast.info(direction === -1 ? '已经是第一个作品了' : '已经是最后一个作品了');
            return false;
        } finally {
            if (version === this.detailNavigationVersion) this.detailSwitching = false;
        }
    };

    commitDetailWork = (direction: -1 | 1) => {
        const list = this.getDetailList();
        const index = list.items.findIndex(item => String(item.id) === String(this.state.currentImgDetail?.id));
        const next = list.items[index + direction];
        if (index >= 0 && next) this.currentItemClick(next, this.detailListSource, true);
    };

    getCurrentLiveUrl = (work = this.state.currentImgDetail) => {
        const getNestedMediaUrl = (media) => {
            if (!media) {
                return '';
            }
            if (typeof media === 'string') {
                return media;
            }
            return media?.liveUrl
                || media?.live_url
                || media?.ossVideoUrl
                || media?.oss_video_url
                || media?.videoUrl
                || media?.video_url
                || media?.playUrl
                || media?.play_url
                || media?.sourceUrl
                || media?.source_url
                || media?.fileUrl
                || media?.file_url
                || media?.url
                || '';
        };

        return work?.liveUrl
            || work?.live_url
            || work?.ossVideoUrl
            || work?.oss_video_url
            || work?.video?.url
            || work?.video?.ossVideoUrl
            || work?.video?.videoUrl
            || (typeof work?.video === 'string' ? work?.video : '')
            || work?.videoUrl
            || work?.video_url
            || work?.playUrl
            || work?.play_url
            || work?.live?.url
            || work?.live?.videoUrl
            || work?.live?.ossVideoUrl
            || (typeof work?.live === 'string' ? work?.live : '')
            || work?.motionPhoto?.url
            || work?.motionPhoto?.videoUrl
            || (typeof work?.motionPhoto === 'string' ? work?.motionPhoto : '')
            || work?.mediaUrl
            || work?.media_url
            || work?.sourceUrl
            || work?.source_url
            || work?.fileUrl
            || work?.file_url
            || getNestedMediaUrl(work?.media)
            || work?.resource?.liveUrl
            || getNestedMediaUrl(work?.resource)
            || getNestedMediaUrl(work?.file)
            || getNestedMediaUrl(work?.source)
            || getNestedMediaUrl(work?.attachment)
            || '';
    }

    pauseDetailLive = (shouldUpdateState = true) => {
        if (this.detailLiveVideoRef) {
            this.detailLiveVideoRef.pause();
            this.detailLiveVideoRef.currentTime = 0;
        }
        if (shouldUpdateState) {
            this.setState({ detailLivePlaying: false });
        }
    }

    playDetailLive = () => {
        const liveUrl = this.getCurrentLiveUrl();
        if (!liveUrl || !this.detailLiveVideoRef) {
            return;
        }
        this.detailLiveVideoRef.currentTime = 0;
        this.detailLiveVideoRef.muted = this.state.detailLiveMuted;
        this.detailLiveVideoRef.play().then(() => {
            this.setState({ detailLivePlaying: true });
        }).catch(() => {});
    }

    handleDetailLiveClick = () => {
        if (!this.getCurrentLiveUrl()) {
            return;
        }
        if (this.state.detailLivePlaying) {
            this.pauseDetailLive();
            return;
        }
        this.playDetailLive();
    }

    handleDetailLiveEnded = () => {
        this.setState({ detailLivePlaying: false });
    }

    maybeAutoPlayDetailLive = (work) => {
        if (String(work?.type || '').toLowerCase() !== 'live' || !this.getCurrentLiveUrl(work)) {
            return;
        }
        window.setTimeout(() => {
            if (this.state.currentImgDetailShowStatus && String(this.state.currentImgDetail?.type || '').toLowerCase() === 'live') {
                this.playDetailLive();
            }
        }, 80);
    }

    toggleDetailLiveMute = (e: React.MouseEvent) => {
        e.stopPropagation();
        const nextMuted = !this.state.detailLiveMuted;
        this.setState({ detailLiveMuted: nextMuted });
        if (this.detailLiveVideoRef) {
            this.detailLiveVideoRef.muted = nextMuted;
        }
    }

    closeCurrentImgDetail = (afterClose?: () => void) => {
        this.detailRequestId++;
        this.detailNavigationVersion++;
        this.detailSwitching = false;
        this.clearDetailCache();
        this.detailEntryWork = null;
        const { match } = this.props;
        const routerParams = new URLSearchParams(this.props.location.search);
        const workId: any = routerParams?.get('workId') || '';
        this.pauseDetailLive();
        (this.props as any).site?.setCurrentPhotographyWorkId(null);
        this.setState({
            currentImgDetail: {},
            currentImgDetailShowStatus: false,
            currentImgDetailLoading: false,
            detailLivePlaying: false,
            detailLiveMuted: true,
        }, () => {
            if (workId) {
                this.props.history.push(`/photography/${match.params.code}`);
            }
            afterClose && afterClose();
        });
    }
    // 作品列表为空
    worksListNull = (style = {}) => {
        return (
            <div className={styles.nullBox} style={style}>
                <img src={photographyImage.emptyWorks} alt="" className={styles.nullIcon} />
                <div className={styles.nullTip}>暂无作品，去vivo社区App端投稿吧</div>
                <div className={styles.openAppBtn} onClick={() => {
                    this.setState({
                        tipPopupStatus: true,
                        currentOpenAppType: 1
                    })
                }}>打开App端去投稿</div>
            </div>
        )
    }

    // 投票列表为空
    votesListNull = () => {
        return (
            <div className={styles.nullBox}>
                <img src={`${BASE_IMG_URL}/photography/null5.png`} alt="" className={styles.nullIcon} />
                <div className={styles.nullTip}>去vivo社区App端支持你喜欢的作品吧</div>
                <div className={styles.openAppBtn} onClick={() => {
                    this.setState({
                        tipPopupStatus: true,
                        currentOpenAppType: 2
                    })
                }}>打开App端去投票</div>
            </div>
        )
    }

    renderDetailInfo = (currentImgDetail: any) => {
        const isCurrentVideo = isVideoWork(currentImgDetail);
        return (
            <div className={styles.detailInfoBox}>
                <div className={styles.userInfo}>
                    {currentImgDetail.user?.avatar && (<>
                        <Avatar
                            avatarUrl={currentImgDetail.user?.avatar}
                            userCode={currentImgDetail.user?.code}
                            size={60}
                            titleMark={currentImgDetail.user?.titleMark}
                            level={currentImgDetail.user?.level}
                            officialSize={24}
                            isOpenBlank={true}
                        ></Avatar>
                    </>)}
                    <div className={styles.userNameBox}>
                        <span className={styles.nickname}>{currentImgDetail.user?.nickname}</span>
                        <span className={styles.titleName}>{currentImgDetail.user?.titleName}</span>
                    </div>
                </div>
                <div className={styles.numBox}>
                    {Number(currentImgDetail.ranking) > 0 && (!isCurrentVideo || Number(currentImgDetail.status) === 1) && <div className={styles.waterfallRanking}>第{currentImgDetail.ranking > 100 ? '100+' : currentImgDetail.ranking}名</div>}
                    <div className={styles.waterfallNumber}>ID:{currentImgDetail.number}</div>
                    {(!isCurrentVideo || Number(currentImgDetail.status) === 1) && <div className={styles.waterfallVoteCount}>{currentImgDetail.voteCount || 0}票</div>}
                </div>
            </div>
        );
    };

    renderDetailFooter = (currentImgDetail: any) => {
        const { user } = this.props;
        const { photographyDetail, shareLinkType } = this.state;
        const isCurrentVideo = isVideoWork(currentImgDetail);
        const detailCloseButton = <button type="button" className={isCurrentVideo ? styles.videoDetailReturn : styles.detailClose} aria-label="关闭作品详情" onClick={() => this.closeCurrentImgDetail()}>
            {isCurrentVideo ? <PhotographyMediaIcon kind="return" /> : <img src={`${BASE_IMG_URL}/photography/back3.png`} alt="" />}
        </button>;
        return <>
            {user?.userInfo?.code != currentImgDetail.user?.code && <div className={styles.detailBtnBox} data-photography-actions="true">
                {detailCloseButton}

                {/* 有参数时显示"我要参赛" */}
                {shareLinkType && <div className={classNames(styles.canvass, photographyDetail.status == 2 ? styles.canvassGray : '')} onClick={() => {
                    this.setState({
                        currentOpenAppType:1
                    },()=>{
                         this.handleOpenAppConfirm()
                    })
                   }}>我要参赛</div>}
                {/* 没参数时显示"为作者拉票" */}
                {!shareLinkType && <div className={classNames(styles.canvass, photographyDetail.status == 2 ? styles.canvassGray : '')} onClick={() => this.canvassForVotesClick(currentImgDetail)}>为作者拉票</div>}
                <div className={classNames(styles.openAppVote, (photographyDetail.status == 2 || currentImgDetail.isVoted) ? styles.openAppVoteGray : '')} onClick={() =>{
                    if(photographyDetail.status == 2) {
                        Toast.info('活动已结束');
                        return false;
                    }
                    this.voteClick(currentImgDetail)}
                }

                >{!currentImgDetail.isVoted ? '投票' : '今日已投'}</div>
            </div>}
            {user?.userInfo?.code == currentImgDetail.user?.code && <div className={styles.detailBtnBox} data-photography-actions="true">
                {detailCloseButton}
                {currentImgDetail.status == 1 &&<div className={classNames(styles.mycanvass, photographyDetail.status == 2 ? styles.mycanvassGray : '')} onClick={() => this.canvassForVotesClick(currentImgDetail)}>我要拉票</div>}

                {currentImgDetail.status == 0 && <div className={classNames(styles.waterfallStatus)}>
                    <img
                        src={isCurrentVideo ? photographyVideoImage.pending : `${BASE_IMG_URL}/photography/icon6.png`}
                        alt="审核中"
                        className={styles.icon}
                    />审核中
                </div>}
                {currentImgDetail.status == 2 && <div className={classNames(styles.waterfallStatus, styles.waterfallStatus2)}>
                    <img
                        src={isCurrentVideo ? photographyVideoImage.warning : `${BASE_IMG_URL}/photography/icon7.png`}
                        alt="审核中"
                        className={styles.icon}
                    />
                    审核未通过
                </div>}
                {currentImgDetail.status == 3 && <div className={classNames(styles.waterfallStatus, styles.waterfallStatus2)}>
                    <img
                        src={isCurrentVideo ? photographyVideoImage.warning : `${BASE_IMG_URL}/photography/icon7.png`}
                        alt="审核中"
                        className={styles.icon}
                    />
                    已下架

                </div>}
                {/* 删除按钮显示逻辑：
                    1. 是自己的作品
                    2. 排名为0（未上榜）
                    3. 活动未结束 或 活动已结束但作品状态不是审核通过（审核中/不通过/已下架可删除）
                */}
                {(currentImgDetail.user?.code == user?.userInfo?.code &&
                  (photographyDetail.status != 2 || currentImgDetail.status != 1)
                ) && (isCurrentVideo
                    ? <button type="button" className={styles.videoDetailMore} aria-label="更多操作" onClick={() => this.manageCurrentWork(currentImgDetail)}><img src={photographyVideoImage.more} alt="" /></button>
                    : <img src={`${BASE_IMG_URL}/photography/icon11.png`} alt="" className={styles.detailMore} onClick={() => this.manageCurrentWork(currentImgDetail)} />)}

            </div>}
        </>;
    };

    renderDetailPreview = (item: any) => {
        if (!item) return undefined;
        const work = { ...item, ...this.getCachedDetail(item.id) };
        const video = isVideoWork(work);
        const aiText = getVideoAiText(work.aigcStatus);
        return <div className={classNames(styles.waterfallCardDetail, video && styles.videoDetail, video && aiText && styles.videoHasAi)}>
            <div className={styles.detailInfoOuterBox}>
                <div className={styles.detailImgBox}>
                    <img src={updateImageListSrc(work.image, 10)} alt="" decoding="async" draggable={false} className={video ? styles.videoSwipePoster : styles.detailImg} />
                </div>
                {this.renderDetailInfo(work)}
                {video && aiText && <div className={styles.videoAi}><img src={photographyVideoImage.ai} alt="" />{aiText}</div>}
                {video && <div className={styles.videoProgress} />}
            </div>
            {this.renderDetailFooter(work)}
        </div>;
    };

    render() {
        let { user, site } = this.props;
        const downloadAppStatus = site?.appDownloadShowStatus ?? false;
        const {  scrollY, sharePosterVisible, shareWorkItem,photoList, loading, hasMore, photographyDetail, rulePopupStatus, navList, navCurrentIndex, tipPopupStatus, isFoldWorkList, myWorkHasMore, myWorkLoading, myWorkError, myWorksList, myVotesList, myVoteHasMore, myVoteLoading, myVoteError, deleteWorkPopupStatus, manageWorkPopupStatus, closeDetailTrigger, userLatestWork, currentImgDetailShowStatus, currentImgDetail, currentOpenAppType, weiXinTipMask, availableVotes, floatingLikes, likeAnimationVisible, shareLinkType, detailLivePlaying, detailLiveMuted, downloadAppStatus: _unused, isVivoBrowserStatus } = this.state;
        const isCurrentLive = String(currentImgDetail?.type || '').toLowerCase() === 'live';
        const isCurrentVideo = isVideoWork(currentImgDetail);
        const isVideoManagement = isVideoWork(this.state.manageCurrentWorkData);
        const currentLiveUrl = this.getCurrentLiveUrl(currentImgDetail);
        const detailWorks = this.getDetailList().items;
        const detailIndex = detailWorks.findIndex(item => String(item.id) === String(currentImgDetail?.id));
        const previousWork = detailWorks[detailIndex - 1];
        const nextWork = detailWorks[detailIndex + 1];
        const nextVideoUrl = nextWork && isVideoWork(nextWork) ? getPhotographyVideoUrl(this.getCachedDetail(nextWork.id) || nextWork) : '';
        let options = {};
        const parserOptions = {
            replace: (domNode: any) => {
                // 可以在这里自定义节点替换逻辑
            },
            ...options
        };
        return (
            <div className={styles.wrapper} style={{ paddingTop: downloadAppStatus ? px2rem(100) : 0 }}>
                <div className={styles.photographyImgDetail}>
                    <div className={styles.detailTiTleBox}>
                        <img src={`${BASE_IMG_URL}/photography/home2.png`} alt="" className={styles.detailTiTleBack}  onClick={() => {
                            window.ensureBack(() => {});
                        }}/>
                    </div>
                    <img src={updateImageListSrc(photographyDetail.cover, 20)} alt="" className={styles.photographyImg}/>
                    <div className={styles.enterInfoBox}>
                        <div className={styles.enterItem} onClick={() => {
                            this.setState({
                                rulePopupStatus: true
                            })
                        }}>
                            <img src={`${BASE_IMG_URL}/photography/icon4.png`} alt="" className={styles.enterIcon}/>
                            <span className={styles.enterText}>活动规则</span>
                        </div>
                        {photographyDetail.status == 1 && <div className={styles.enterItem} onClick={() => {
                            if(availableVotes == 0) {
                                Toast.info("今日可用票0，明天再来吧")
                            } else {
                                Toast.info({
                                    content: `今日可用票${availableVotes}，去为你喜爱的作品投票吧`,
                                    icon: `${BASE_IMG_URL}/photography/icon10.png`
                                })
                            }
                        }}>
                            <img src={`${BASE_IMG_URL}/photography/icon5.png`} alt=""  className={styles.enterIcon}/>
                            <span className={styles.enterText}>可用票{availableVotes}</span>
                        </div>}
                        {(photographyDetail.status == 2 && photographyDetail.exhibition?.id) && <div className={styles.enterItem} onClick={() => {
                            this.props.history.push(`/photoExhibition/${photographyDetail.exhibition?.id}`);
                        }}>
                            <img src={`${BASE_IMG_URL}/photography/icon9.png`} alt=""  className={styles.enterIcon}/>
                            <span className={styles.enterText}>观看影展</span>
                        </div>}
                    </div>
                    {photographyDetail.status == 2 && <img src={`${BASE_IMG_URL}/photography/end.png`} alt="" className={styles.statusIcon} />}
                    
                </div>

                {/* tab切换 */}
                {scrollY > (this.navBoxRef?.offsetTop || 600) && <div className={styles.fixedNavFilterBox} style={{ top: downloadAppStatus ? px2rem(88) : 0 }}>
                    {/* <div className={styles.detailTiTleBox}>
                        <img src={`${BASE_IMG_URL}/photography/back.png`} alt="" className={styles.detailTiTleBack}  onClick={() => {
                            
                        }}/>
                        <div className={styles.detailTiTle}>{photographyDetail.name}</div>
                    </div> */}
                    <div className={styles.navBox}>
                        <TabNav navList={navList} isAlignLeft={true} currentIndex={navCurrentIndex} navClick={(item:any, index:any) => this.navClick(item, index)}></TabNav>
                        <img src={`${BASE_IMG_URL}/photography/search.png`} alt="" className={styles.searchIcon} onClick={this.goSearchPage}/>
                    </div>    
                </div>}
                <div className={styles.navBox} ref={(ref) => { this.navBoxRef = ref; }}>
                    <TabNav navList={navList} isAlignLeft={true} currentIndex={navCurrentIndex} navClick={(item:any, index:any) => this.navClick(item, index)}></TabNav>
                    <img src={`${BASE_IMG_URL}/photography/search.png`} alt="" className={styles.searchIcon} onClick={this.goSearchPage}/>
                </div>

                {/* 规则弹框 */}
                {rulePopupStatus && <div className={styles.popupBox}>
                    <div className={styles.popupMask} onClick={() => this.onHidePopup()}></div>
                    <div className={styles.popupContent}>
                        <div className={styles.popupTitle}>活动规则</div>
                        <div className={styles.richtext}>
                            {parse(photographyDetail?.rules || '', parserOptions)}
                        </div>
                        <div className={styles.popupBtn} onClick={() => this.onHidePopup()}>我知道了</div>
                    </div>
                </div>}

                {/* 作品管理弹框 */}
                <CommonPopupNew
                    visible={manageWorkPopupStatus}
                    title="更多操作"
                    confirmText="删除作品"
                    popupContentClassName={classNames(styles.workActionPopup, styles.workMorePopup, isVideoManagement && styles.videoActionPopup)}
                    onCancelChange={() => this.onHidePopup()}
                    onConfirmButtonChange={() => this.deleteWorkOpenPopup()}
                    >
                    {/* children 插槽内容 */}
                </CommonPopupNew>

                {/* 作品删除二次确认弹框 */}
                <CommonPopupNew
                    visible={deleteWorkPopupStatus}
                    title={isVideoManagement ? '确认删除此作品？' : '删除作品'}
                    popupContentClassName={classNames(styles.workActionPopup, isVideoManagement && styles.videoActionPopup)}
                    confirmText={this.state.deletingWork ? '删除中...' : '确定'}
                    confirmDisabled={this.state.deletingWork}
                    onCancelChange={() => this.onHidePopup()}
                    onConfirmButtonChange={() => this.deleteWorkConfirm()}
                    >
                    {/* children 插槽内容 */}
                    {!isVideoManagement && <div className={styles.popupDeleteTip}>确定要删除此作品吗？</div>}
                </CommonPopupNew>
                {/* 飘动的爱心图标 */}
                {likeAnimationVisible && <div className={styles.likeBtnOuterBox}>
                    {floatingLikes.map(like => (
                        <img
                            key={like.id}
                            src={`${BASE_IMG_URL}/photography/icon14.png`}
                            alt=""
                            className={styles.floatingLikeIcon}
                            style={{
                                left: `calc(50% + ${like.left}px)`,
                                '--x-offset': `${like.xOffset}px`,
                                '--delay': `${like.delay}ms`
                            } as React.CSSProperties}
                        />
                    ))}
                </div>}
                

                {/* 点击某个item，打开详情 */}
                {currentImgDetailShowStatus && <PhotographyDetailPager
                    workKey={String(currentImgDetail.id)}
                    previous={this.renderDetailPreview(previousWork)} next={this.renderDetailPreview(nextWork)}
                    disabled={manageWorkPopupStatus || deleteWorkPopupStatus || sharePosterVisible || tipPopupStatus}
                    onPrepare={this.prepareDetailWork} onCommit={this.commitDetailWork}>
                    <div className={classNames(styles.waterfallCardDetail, isCurrentVideo && styles.videoDetail, isCurrentVideo && getVideoAiText(currentImgDetail.aigcStatus) && styles.videoHasAi)}>
                        <div className={styles.detailInfoOuterBox}>
                            <div className={styles.detailImgBox}>
                                {/* <img src={currentImgDetail.image} alt="" className={styles.detailImg}/> */}
                                {isCurrentLive && <div className={styles.liveTagBar}>
                                    <div className={styles.liveTag} style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); this.playDetailLive(); }}>
                                        <img className={styles.liveIcon} src={LIVE_ICON_URL} alt="" />
                                        <span>动态照片</span>
                                    </div>
                                    <div className={styles.soundBtn} onClick={this.toggleDetailLiveMute}>
                                        <img className={styles.soundIcon} src={detailLiveMuted ? SOUND_CLOSE_ICON_URL : SOUND_OPEN_ICON_URL} alt="" />
                                    </div>
                                </div>}
                                {isCurrentVideo ? <PhotographyVideoPlayer
                                    key={String(currentImgDetail.id)}
                                    src={getPhotographyVideoUrl(currentImgDetail)}
                                    sourceLoading={this.state.currentImgDetailLoading}
                                    poster={updateImageListSrc(currentImgDetail.image, 10)}
                                    title={currentImgDetail.name}
                                    width={currentImgDetail.video?.width || currentImgDetail.width}
                                    height={currentImgDetail.video?.height || currentImgDetail.height}
                                    progressHost={this.videoProgressRef}
                                    paused={manageWorkPopupStatus || deleteWorkPopupStatus || sharePosterVisible || tipPopupStatus}
                                /> : isCurrentLive && currentLiveUrl && !isVivoBrowserStatus ? (<>
                                    <div className={styles.liveMediaBox} onClick={this.handleDetailLiveClick}>
                                        <video
                                            ref={(el) => { this.detailLiveVideoRef = el; }}
                                            src={currentLiveUrl}
                                            className={styles.liveVideo}
                                            playsInline
                                            webkit-playsinline="true"
                                            x5-playsinline="true"
                                            x5-video-player-type="h5"
                                            x5-video-player-fullscreen="false"
                                            muted={detailLiveMuted}
                                            poster={updateImageListSrc(currentImgDetail.image, 10)}
                                            onEnded={this.handleDetailLiveEnded}
                                        />
                                        {!detailLivePlaying && (
                                            <img
                                                src={updateImageListSrc(currentImgDetail.image, 10)}
                                                alt={currentImgDetail.name || ''}
                                                className={styles.liveCoverImage}
                                            />
                                        )}
                                    </div>
                                </>) : isCurrentLive ? (
                                    <img
                                        src={updateImageListSrc(currentImgDetail.image, 10)}
                                        alt={currentImgDetail.name || ''}
                                        className={styles.detailImg}
                                    />
                                ) : (
                                    <Image
                                        src={updateImageListSrc(currentImgDetail.image, 10)}
                                        className={styles.detailImg}
                                        fallback={`${BASE_IMG_URL}/common/pic_place1.png`}
                                        preview={{ mask: '' }}
                                    />
                                )}
                                {!isCurrentVideo && currentImgDetail.aigcStatus == 1 && <div className={styles.detailImgAi}><span>图片属于AI生成</span></div>}
                                {!isCurrentVideo && currentImgDetail.aigcStatus == 2 && <div className={styles.detailImgAi}><span>作者声明：图片由AI生成</span></div>}
                                {!isCurrentVideo && currentImgDetail.aigcStatus == 3 && <div className={styles.detailImgAi}><span>图片疑似使用AI生成技术，请谨慎甄别</span></div>}
                            </div>
                            {this.renderDetailInfo(currentImgDetail)}
                            {isCurrentVideo && getVideoAiText(currentImgDetail.aigcStatus) && <div className={styles.videoAi}>
                                <img src={photographyVideoImage.ai} alt="" />{getVideoAiText(currentImgDetail.aigcStatus)}
                            </div>}
                            {isCurrentVideo && <div ref={this.videoProgressRef} className={styles.videoProgress} />}
                        </div>
                        {this.renderDetailFooter(currentImgDetail)}
                    </div>
                </PhotographyDetailPager>}
                {currentImgDetailShowStatus && nextVideoUrl && <PhotographyVideoWarmup key={nextVideoUrl} src={nextVideoUrl} />}
                
                {/* 无作品时，不显示投稿按钮；人气排行未开启时也不显示 */}
                {(photographyDetail.status == 1 && !userLatestWork?.id && !(navCurrentIndex === 1 && !photographyDetail.isRankingEnabled)) && <button type="button" aria-label="上传作品" className={styles.addPhotography} onClick={() => {
                    this.setState({
                        tipPopupStatus: true,
                        currentOpenAppType: 1
                    })
                }}><img src={`${BASE_IMG_URL}/common/publish2.png`} alt="" /></button>}
                <CommonPopupNew
                    visible={tipPopupStatus}
                    title={currentOpenAppType == 2 ? "<p>请前往vivo社区App端</br>为喜爱的作品投票</p>" :"请前往vivo社区App端投稿吧"}
                    confirmText="打开App"
                    onCancelChange={() => this.onHidePopup()}
                    onConfirmButtonChange={() => this.handleOpenAppConfirm()}
                    >
                    {/* children 插槽内容 */}
                    
                </CommonPopupNew>

                {/* 为0是最新作品列表 */}
                <PhotographyWorkListBackdrop frozen={currentImgDetailShowStatus}>
                {navCurrentIndex == 0 && <> 
                    {photoList.length > 0 && <WaterfallFlow
                        data={photoList}
                        loading={loading}
                        hasMore={hasMore}
                        onLoadMore={this.handleLoadMore}
                        searchKeyword="" // 如果需要搜索功能，可以添加搜索关键词
                        voteClick={(item) =>this.voteClick(item)}
                        photographyDetail={photographyDetail}
                        currentItemClick={(item) =>this.currentItemClick(item)}
                        canvassForVotesClick={(item) => this.canvassForVotesClick(item)}
                    />}
                    {photoList.length == 0 && this.worksListNull()}
                </>}

                {/* 为1是人气排行列表 */}
                {navCurrentIndex == 1 && <div className={styles.rankingListBox} style={{padding: !photographyDetail.isRankingEnabled ? '0' : px2rem(24)}}>
                    {/* 当未开启人气排行时显示正在核算 */}
                    {!photographyDetail.isRankingEnabled && <div className={styles.nullBox} style={{marginBottom: px2rem(24)}}>
                        <img src={`${BASE_IMG_URL}/photography/null4.png`} alt="" className={classNames(styles.nullIcon)} />
                        <div className={styles.nullTip}>正在核算票数，<br/>最终榜单即将公布！</div>
                    </div>}
                    {/* 当已开启人气排行时显示列表 */}
                    {(photographyDetail.isRankingEnabled && photoList.length > 0) && <PhotographyList
                        data={photoList}
                        loading={loading}
                        hasMore={hasMore}
                        onLoadMore={this.handleLoadMore}
                        voteClick={(item) =>this.voteClick(item)}
                        isShowTopThree={true}
                        showVideoIcon={false}
                        currentItemClick={(item) =>this.currentItemClick(item)}
                        photographyDetail={photographyDetail}
                        canvassForVotesClick={(item) =>this.canvassForVotesClick(item)}
                    />}
                    {(photographyDetail.isRankingEnabled && photoList.length == 0) && this.worksListNull({margin: 0})}
                    {/* 我的最新作品 */}
                    {userLatestWork?.id && <div className={styles.myNewWork}>
                        <div className={styles.photographyCard}>
                            <div onClick={() => {
                                    // this.setState({
                                    //     currentImgDetail: userLatestWork,
                                    //     currentImgDetailShowStatus: true
                                    // })
                            }} className={styles.cardLeft}>
                                <div className={styles.photographyIndex}>{userLatestWork.ranking > 100 ? '100+' : userLatestWork.ranking}</div>
                                {userLatestWork.image && (
                                    <div className={styles.photographyImgBox}>
                                        {String(userLatestWork?.type || '').toLowerCase() === 'live' && <img src={LIVE_ICON_URL} alt="" className={styles.liveTagIcon} />}
                                        <img 
                                            src={userLatestWork.image} 
                                            alt={userLatestWork.name}
                                            className={styles.photographyImg}
                                            onError={(e) => {
                                                e.currentTarget.src = `${BASE_IMG_URL}/common/null.png`;
                                            }}
                                        />
                                    </div>
                                )}
                                
                                <div className={styles.photographyInfo}>  
                                    <div className={styles.infoTopBox}>
                                        <div className={styles.photographyNumber}>ID:{userLatestWork.number}</div>
                                        <div className={styles.photographyName}>{userLatestWork.name}</div>
                                    </div>
                                    <div className={styles.progressOuterBox}>
                                        <div className={styles.progressBox}>
                                            <div className={styles.progress} style={{width: userLatestWork.progress + '%'}}></div>
                                        </div>
                                        <div className={styles.photographyVoteCount}>{userLatestWork.voteCount}票</div>
                                    </div>
                                </div>
                            </div>
                            {photographyDetail.status == 1 && <div className={classNames(styles.voteBtn)} onClick={() => this.canvassForVotesClick(userLatestWork)}>我要拉票</div>}
                        </div>
                        
                    </div>}
                </div>}

                {/* 为2是我的，包含我的投稿和我的投票 */}
                {navCurrentIndex == 2 && <div className={styles.listBox}>
                    {myWorksList.length > 0 && <div className={styles.myTitleBox}>
                        <div className={styles.title}>我的作品</div>
                        {(myWorksList.length > 2 || myWorkHasMore) && <button type="button" className={styles.foldWorksButton} aria-label={isFoldWorkList ? '展开我的作品' : '收起我的作品'} aria-expanded={!isFoldWorkList} onClick={() => {
                            this.setState({ isFoldWorkList: !isFoldWorkList });
                            if (isFoldWorkList && myWorksList.length <= 2 && myWorkHasMore) this.loadMoreMyWorksList();
                        }}>
                            <img src={`${BASE_IMG_URL}/photography/jt1.png`} alt="" className={classNames(styles.jtIcon, isFoldWorkList ? styles.jtRotate : '')} />
                        </button>}
                    </div>}
                    {myWorksList.length > 0 && <WaterfallFlow
                        data={isFoldWorkList ? myWorksList.slice(0, 2) : myWorksList}
                        hasMore={false}
                        searchKeyword="" // 如果需要搜索功能，可以添加搜索关键词
                        voteClick={(item) =>this.voteClick(item)}
                        photographyDetail={photographyDetail}
                        type="2"
                        currentItemClick={(item) =>this.currentItemClick(item)}
                        canvassForVotesClick={(item) =>this.canvassForVotesClick(item)}
                        manageCurrentWork={(item) =>this.manageCurrentWork(item)}
                        closeDetailTrigger={closeDetailTrigger}
                    />}
                    {(isFoldWorkList ? myWorksList.length > 2 || myWorkHasMore : myWorkHasMore) && myWorksList.length > 0 && !myWorkLoading && !myWorkError && <button type="button" className={styles.loadMoreMyWork} onClick={() => {
                        if (isFoldWorkList) {
                            this.setState({ isFoldWorkList: false });
                            if (myWorksList.length <= 2 && myWorkHasMore) this.loadMoreMyWorksList();
                        } else this.loadMoreMyWorksList();
                    }}>
                        <span className={styles.text}>点击加载更多作品</span>
                        <img src={`${BASE_IMG_URL}/photography/jt2.png`} alt="" className={styles.icon}/>
                    </button>}
                    {myWorksList.length > 0 && this.renderMyListFeedback('works')}
                    {myWorksList.length == 0 && <div className={styles.photoListBox}>
                        <div className={styles.photoTitle}>我的作品</div>
                        {this.renderMyListFeedback('works')}
                        {!myWorkLoading && !myWorkError && this.worksListNull({marginBottom: px2rem(24)})}
                    </div>}
                    <div className={styles.photoListBox} style={{ marginBottom: myVotesList.length == 0 ? px2rem(24) : '0'}}>
                        <div className={styles.photoTitle}>我的投票</div>
                        {myVotesList.length > 0 && <>
                            <div className={styles.photoTitleBox}>
                                <div className={styles.titleLeft}>
                                    <div className={styles.photoTag}>排名</div>
                                    <div className={styles.photoTag}>作品</div>
                                </div>
                                {photographyDetail.status == 1 && <div className={styles.photoTag}>投票</div>}
                            </div>
                            <PhotographyList
                                data={myVotesList}
                                showVideoIcon={false}
                                showWorkRanking
                                loading={myVoteLoading}
                                hasMore={myVoteHasMore && !myVoteError}
                                onLoadMore={this.loadMoreMyVotesList}
                                voteClick={(item) =>this.voteClick(item)}
                                currentItemClick={(item) =>this.currentItemClick(item, 'myVotes')}
                                photographyDetail={photographyDetail}
                                canvassForVotesClick={(item) =>this.canvassForVotesClick(item)}
                            />
                        </>}
                        {this.renderMyListFeedback('votes')}
                        {myVotesList.length == 0 && !myVoteLoading && !myVoteError && this.votesListNull()}
                    </div>
                    
                </div>}

                </PhotographyWorkListBackdrop>
                <SharePosterPopup
                    visible={sharePosterVisible}
                    workData={shareWorkItem}
                    photographyDetail={photographyDetail}
                    shareUrl={this.getShareUrl(shareWorkItem)}
                    topVoteCount={this.getTopVoteCount()}
                    onClose={this.closeSharePoster}
                />

                {/* 微信内提示弹窗 */}
                {weiXinTipMask && this.isWeixinXml()}

            </div>
        );
    }
}

export default withRouter(PhotographyH5Page);
